'use client'

import { PropsWithChildren, ReactNode, Suspense, use, useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import { CLASSES } from '@/config/html-attributes.config'
import { EVENTS_SLUG_POSTFIX } from '@/config/navigation.config'
import classNames from 'classnames'
import { canUseWebP } from '@/utils/browser.utils'
import { MWCookies } from '@/app/_actions/get-cookies.action'
import { APP_VERSION } from '@/config/environments.config'
import useIsHydrated from '@/hooks/useIsHydrated'

type Props = {
  mwCookiesPromise: Promise<MWCookies>
}

const BodyWrapper = ({ children, mwCookiesPromise }: PropsWithChildren<Props>): ReactNode => {
  const pathname = usePathname()
  const isEvents = useMemo(() => pathname.includes(`/${EVENTS_SLUG_POSTFIX}/`), [pathname])
  const mwCookies = use(mwCookiesPromise)
  const isMobileAppView = mwCookies.isMobileAppView

  const isHydrated = useIsHydrated()

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    window.__NEXT_HYDRATED = true
  }, [])

  return (
    <body
      data-apv={APP_VERSION}
      className={classNames(
        'loaded',
        'ready',
        {
          [CLASSES.MOBILE_APP]: isMobileAppView,
          [CLASSES.EVENTS]: isEvents,
          [CLASSES.CAN_USE_WEBP]: isHydrated ? canUseWebP() : false,
        },
      )}>
      {children}
    </body>
  )
}

const BodyWrapperWithSuspense = ({ children, mwCookiesPromise }: PropsWithChildren<Props>): ReactNode => {
  return (
    <Suspense fallback={<body />}>
      <BodyWrapper mwCookiesPromise={mwCookiesPromise}>
        {children}
      </BodyWrapper>
    </Suspense>
  )
}

export default BodyWrapperWithSuspense
